// @types
import { ContactFormValues } from 'modules/common/forms/types/contact'
// @libraries
import * as Yup from 'yup'
import libphonenumber from 'google-libphonenumber'

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance()
const phoneExp = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/

const primaryEmailAddress = Yup.string()
  .email('Please enter your email in a valid format.')
  .required('Email is required!')

const phones = Yup.array().of(
  Yup.object()
    .shape(
      {
        countryCode: Yup.string()
          .when(['number', 'type', 'location'], {
            is: (number, type, location) => {
              return !!number || !!type || !!location
            },
            then: Yup.string().required('Country Code is required!'),
            otherwise: Yup.string(),
          })
          .test(
            'valid-country-code',
            'Not a valid country code',
            function (number) {
              if (!number) return true

              return phoneUtil.getSupportedRegions().some((countryCode) => {
                return (
                  phoneUtil.getCountryCodeForRegion(countryCode).toString() ===
                  number
                )
              })
            },
          ),
        number: Yup.string()
          .when(['countryCode', 'type', 'location'], {
            is: (countryCode, type, location) => {
              return !!countryCode || !!type || !!location
            },
            then: Yup.string().required('Number is required!'),
            otherwise: Yup.string(),
          })
          .matches(phoneExp, {
            message: 'Phone number is not valid',
            excludeEmptyString: true,
          }),
        type: Yup.string().when(['countryCode', 'number', 'location'], {
          is: (countryCode, number, location) => {
            return !!countryCode || !!number || !!location
          },
          then: Yup.string().required('Type is required!'),
          otherwise: Yup.string(),
        }),
        location: Yup.string().when(['countryCode', 'number', 'type'], {
          is: (countryCode, number, type) => {
            return !!countryCode || !!number || !!type
          },
          then: Yup.string().required('Location is required!'),
          otherwise: Yup.string(),
        }),
      },
      [
        ['number', 'type'],
        ['number', 'countryCode'],
        ['number', 'location'],
        ['type', 'countryCode'],
        ['type', 'location'],
        ['countryCode', 'location'],
      ],
    )
    .test('check-unique-phone-type', '', function (currentPhone) {
      if (!currentPhone) return true
      // @ts-ignore
      const allPhones: ContactFormValues['phones'] = this.parent
      const otherPhones = allPhones.filter((phone) => phone !== currentPhone)
      const isDupePhone = otherPhones.some((phone) => {
        if (
          phone.type === '' ||
          currentPhone.type === '' ||
          phone.location === '' ||
          currentPhone.location === ''
        ) {
          return false
        }

        return (
          phone.type === currentPhone.type &&
          phone.location === currentPhone.location
        )
      })

      if (isDupePhone) {
        return this.createError({
          path: `${this.path}.type`,
          message: 'Type has already been selected',
        })
      }

      return true
    })
    .test('check-unique-phone-location', '', function (currentPhone) {
      if (!currentPhone) return true

      const allPhones: ContactFormValues['phones'] = this.parent
      const otherPhones = allPhones.filter((phone) => phone !== currentPhone)
      const isDupePhone = otherPhones.some((phone) => {
        if (
          phone.type === '' ||
          currentPhone.type === '' ||
          phone.location === '' ||
          currentPhone.location === ''
        ) {
          return false
        }

        return (
          phone.type === currentPhone.type &&
          phone.location === currentPhone.location
        )
      })

      if (isDupePhone) {
        return this.createError({
          path: `${this.path}.location`,
          message: 'Location has already been selected',
        })
      }

      return true
    }),
)

const addresses = Yup.array().of(
  Yup.object().shape({
    addressType: Yup.string().required('Type of address is required!'),
    countryCode: Yup.string().required('Country is required!'),
    address1: Yup.string().required('Address is required!'),
    city: Yup.string().required('City is required!'),
    postalCode: Yup.string().required('Postal code is required!'),
    state: Yup.string().when(['countryCode'], {
      is: (countryCode) => {
        return countryCode === 'CA' || countryCode === 'US'
      },
      then: Yup.string().required('State is required!'),
      otherwise: Yup.string(),
    }),
  }),
)

export const validationSchema = Yup.object().shape({
  primaryEmailAddress,
  phones,
  addresses,
})
